import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Save, Eye, EyeOff, Send, Shield, Mail, Server, Clock, User,
  RotateCcw, AlertTriangle, CheckCircle, XCircle, Lock, Unlock, History,
  FileText, Building2, Users, Bell, Briefcase, Search, Download, X, Filter, Pencil
} from "lucide-react";

// ═══ CAT_META for System Emails Inventory ═══
const CAT_META = {
  sistema: { label: "Sistema", icon: Server, color: "bg-slate-100 text-slate-700 border-slate-300" },
  contacto: { label: "Contacto", icon: Mail, color: "bg-blue-50 text-blue-700 border-blue-200" },
  legal: { label: "Legal", icon: Shield, color: "bg-purple-50 text-purple-700 border-purple-200" },
  centros: { label: "Centros", icon: Building2, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  asesores: { label: "Asesores", icon: Users, color: "bg-amber-50 text-amber-700 border-amber-200" },
  notificaciones: { label: "Notificaciones", icon: Bell, color: "bg-orange-50 text-orange-700 border-orange-200" },
  plantillas: { label: "Plantillas", icon: FileText, color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  rrhh: { label: "RRHH", icon: Briefcase, color: "bg-pink-50 text-pink-700 border-pink-200" },
};

export default function EmailConfigPage() {
  const { axiosAuth } = useAuth();
  const [activeTab, setActiveTab] = useState("config");

  // ═══ CONFIG TAB STATE ═══
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [revealedFields, setRevealedFields] = useState({});
  const [editedSensitive, setEditedSensitive] = useState({});

  // ═══ TEMPLATES TAB STATE ═══
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(true);
  const [savingTpl, setSavingTpl] = useState(null);

  // ═══ INVENTORY TAB STATE ═══
  const [invData, setInvData] = useState({ emails: [], total: 0 });
  const [invLoading, setInvLoading] = useState(true);
  const [invSearch, setInvSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  // ═══ LOAD CONFIG ═══
  const loadConfig = useCallback(async () => {
    try {
      const r = await axiosAuth().get("/email-config");
      setConfig(r.data);
    } catch { toast.error("Error al cargar configuracion"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  // ═══ LOAD TEMPLATES ═══
  const loadTemplates = useCallback(async () => {
    try {
      const r = await axiosAuth().get("/email-templates");
      setTemplates(r.data);
    } catch { toast.error("Error al cargar plantillas"); }
    finally { setTplLoading(false); }
  }, [axiosAuth]);

  // ═══ LOAD INVENTORY ═══
  const loadInventory = useCallback(async () => {
    try {
      const r = await axiosAuth().get("/audit/emails");
      setInvData(r.data);
    } catch {}
    finally { setInvLoading(false); }
  }, [axiosAuth]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  useEffect(() => {
    if (activeTab === "templates" && tplLoading) loadTemplates();
    if (activeTab === "inventory" && invLoading) loadInventory();
  }, [activeTab, tplLoading, invLoading, loadTemplates, loadInventory]);

  // ═══ CONFIG HANDLERS ═══
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        provider: config.provider, sender_email: config.sender_email,
        sender_name: config.sender_name, reply_to: config.reply_to,
        smtp_host: config.smtp_host, smtp_port: config.smtp_port,
        smtp_username: config.smtp_username, smtp_tls: config.smtp_tls,
        notification_email: config.notification_email,
      };
      if (editedSensitive.resend_api_key) payload.resend_api_key = editedSensitive.resend_api_key;
      if (editedSensitive.smtp_password) payload.smtp_password = editedSensitive.smtp_password;
      const r = await axiosAuth().put("/email-config", payload);
      if (r.data.status === "sin_cambios") toast.info("No se han detectado cambios");
      else toast.success(`Guardado (${r.data.changes} cambios)`);
      setEditedSensitive({}); setRevealedFields({}); setConfirmSave(false);
      await loadConfig();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al guardar configuracion";
      toast.error(msg);
    }
    setSaving(false);
  };

  const handleReveal = async (field) => {
    try {
      const r = await axiosAuth().get(`/email-config/reveal/${field}`);
      setRevealedFields(p => ({ ...p, [field]: r.data.value }));
    } catch { toast.error("Error al revelar"); }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error("Introduce un email"); return; }
    setTesting(true); setTestResult(null);
    try {
      const r = await axiosAuth().post("/email-config/test", { to_email: testEmail });
      setTestResult(r.data);
    } catch { setTestResult({ status: "error", message: "Error de conexion" }); }
    setTesting(false);
  };

  const loadAudit = async () => {
    try {
      const r = await axiosAuth().get("/email-config/audit?limit=30");
      setAuditLogs(r.data.logs || []); setShowAudit(true);
    } catch { toast.error("Error al cargar historial"); }
  };

  const handleRestore = async (auditId) => {
    try {
      const r = await axiosAuth().post(`/email-config/restore/${auditId}`);
      toast.success(`Restaurados ${r.data.fields?.length || 0} campos`);
      await loadConfig();
    } catch { toast.error("Error al restaurar"); }
  };

  // ═══ TEMPLATE HANDLERS ═══
  const handleSaveTpl = async (tpl) => {
    setSavingTpl(tpl.type);
    try {
      await axiosAuth().put(`/email-templates/${tpl.type}`, {
        subject: tpl.subject, body_intro: tpl.body_intro,
        body_footer: tpl.body_footer, phone_display: tpl.phone_display,
      });
      toast.success("Plantilla guardada");
    } catch { toast.error("Error al guardar"); }
    finally { setSavingTpl(null); }
  };

  const updateTpl = (type, field, value) => {
    setTemplates(prev => prev.map(t => t.type === type ? { ...t, [field]: value } : t));
  };

  const typeLabels = {
    reception: { label: "Recepcion de cita", desc: "Email al cliente al solicitar cita", icon: Mail },
    confirmation: { label: "Confirmacion de cita", desc: "Email al confirmar la cita", icon: FileText },
    reminder: { label: "Recordatorio", desc: "Recordatorio antes de la cita. Variables: {hours}", icon: FileText },
    cancellation: { label: "Cancelacion", desc: "Email al cancelar cita", icon: Mail },
    modification: { label: "Solicitud modificacion", desc: "Email al solicitar modificar cita", icon: FileText },
    admin_modification: { label: "Modificacion por admin", desc: "Email al modificar cita confirmada", icon: FileText },
    // Iter 187 (P1) — Plantillas BD para Contacto y Candidaturas
    password_reset: { label: "Restablecer contrasena", desc: "Variables: {name}, {action_url}, {expiry_minutes}", icon: Mail },
    account_activation: { label: "Activacion de cuenta", desc: "Variables: {name}, {role}, {action_url}", icon: Mail },
    password_temporary: { label: "Contrasena temporal", desc: "Variables: {name}, {temp_password}", icon: Mail },
    contact_internal: { label: "Contacto · notificacion interna", desc: "Email a RRHH con los datos de la consulta. Variables: {reason}, {name}, {email}, {phone}, {center}, {message}, {table}, {reference}", icon: Mail },
    contact_confirm: { label: "Contacto · confirmacion al cliente", desc: "Email al cliente confirmando la recepcion. Variables: {name}, {reference}, {confirm_text}", icon: FileText },
    job_internal: { label: "Candidatura · notificacion interna", desc: "Email a RRHH con los datos de la candidatura. Variables: {area}, {name}, {position}, {table}, {reference}", icon: Mail },
    job_confirm: { label: "Candidatura · confirmacion al candidato", desc: "Email al candidato confirmando la recepcion. Variables: {name}, {reference}, {confirm_text}", icon: FileText },
  };

  // ═══ INVENTORY HELPERS ═══
  const filteredEmails = invData.emails.filter(e => {
    const q = invSearch.toLowerCase();
    if (q && !e.address?.toLowerCase().includes(q) && !e.name?.toLowerCase().includes(q) && !e.purpose?.toLowerCase().includes(q)) return false;
    if (filterCategory !== "all" && e.category !== filterCategory) return false;
    return true;
  });

  const exportCSV = () => {
    const header = "Direccion;Nombre;Uso;Flujo;Editable;Activo;Categoria\n";
    const rows = filteredEmails.map(e =>
      `"${e.address}";"${e.name}";"${e.purpose}";"${e.flow}";"${e.editable_from}";"${e.active ? "Si" : "No"}";"${e.category}"`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "emails_sistema.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ═══ SENSITIVE FIELD COMPONENT ═══
  const SensitiveField = ({ field, label, hint, masked, isSet }) => {
    const revealed = revealedFields[field];
    const edited = editedSensitive[field];
    const displayValue = edited !== undefined ? edited : (revealed || masked || "");
    const isHidden = !revealed && !edited && isSet;
    return (
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1.5">
          <Lock className="w-3 h-3 text-amber-500" /> {label}
          {isSet && <Badge variant="outline" className="h-4 text-[8px] border-emerald-200 text-emerald-600 bg-emerald-50 rounded-sm ml-1">Configurado</Badge>}
        </Label>
        <p className="text-[10px] text-slate-400 mb-1">{hint}</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input type={isHidden ? "password" : "text"} value={displayValue}
              onChange={e => setEditedSensitive(p => ({ ...p, [field]: e.target.value }))}
              placeholder={isSet ? "Dejar vacio para mantener" : "Introducir valor"}
              className="rounded-sm h-9 border-slate-200 pr-10 font-mono text-xs" data-testid={`email-${field}-input`} />
            {isSet && !revealed && !edited && (
              <button type="button" onClick={() => handleReveal(field)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid={`reveal-${field}-btn`}>
                <Eye className="w-4 h-4" />
              </button>
            )}
            {(revealed || edited) && (
              <button type="button" onClick={() => { setRevealedFields(p => { const n = {...p}; delete n[field]; return n; }); setEditedSensitive(p => { const n = {...p}; delete n[field]; return n; }); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <EyeOff className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading || !config) return <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-5" data-testid="email-config-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="email-config-title">Centro de Emails</h1>
          <p className="text-sm text-slate-500 mt-0.5">Configuracion, plantillas e inventario del sistema de correo</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start bg-slate-100 rounded-sm h-9 p-0.5">
          <TabsTrigger value="config" className="rounded-sm text-xs uppercase tracking-wider font-semibold data-[state=active]:bg-white" data-testid="tab-config">
            Configuracion
          </TabsTrigger>
          <TabsTrigger value="templates" className="rounded-sm text-xs uppercase tracking-wider font-semibold data-[state=active]:bg-white" data-testid="tab-templates">
            Plantillas
          </TabsTrigger>
          <TabsTrigger value="inventory" className="rounded-sm text-xs uppercase tracking-wider font-semibold data-[state=active]:bg-white" data-testid="tab-inventory">
            Inventario
          </TabsTrigger>
        </TabsList>

        {/* ═══ TAB: CONFIGURACION ═══ */}
        <TabsContent value="config" className="space-y-5 mt-4">
          <div className="flex items-center justify-end gap-2">
            {config.updated_at && (
              <p className="text-[10px] text-slate-400 flex items-center gap-1 mr-auto">
                <Clock className="w-3 h-3" /> Modificado: {new Date(config.updated_at).toLocaleString("es-ES")} por {config.updated_by}
              </p>
            )}
            <Button variant="outline" size="sm" onClick={loadAudit} className="rounded-sm text-[10px] uppercase tracking-wider h-8 px-3" data-testid="audit-log-btn">
              <History className="w-3 h-3 mr-1" /> Historial
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowTestDialog(true)} className="rounded-sm text-[10px] uppercase tracking-wider h-8 px-3" data-testid="test-email-btn">
              <Send className="w-3 h-3 mr-1" /> Probar envio
            </Button>
            <Button size="sm" onClick={() => setConfirmSave(true)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-[10px] uppercase tracking-wider h-8 px-4" data-testid="save-email-config-btn">
              <Save className="w-3 h-3 mr-1" /> Guardar
            </Button>
          </div>

          {/* Sender */}
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-red-600" /> Remitente</CardTitle>
              <CardDescription className="text-xs">Datos del remitente para todos los emails.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email remitente</Label>
                  <Input value={config.sender_email} onChange={e => setConfig(p => ({...p, sender_email: e.target.value}))} className="rounded-sm h-9 border-slate-200" data-testid="sender-email-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Nombre remitente</Label>
                  <Input value={config.sender_name} onChange={e => setConfig(p => ({...p, sender_name: e.target.value}))} className="rounded-sm h-9 border-slate-200" data-testid="sender-name-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Reply-To</Label>
                <Input value={config.reply_to} onChange={e => setConfig(p => ({...p, reply_to: e.target.value}))} placeholder="Mismo que remitente" className="rounded-sm h-9 border-slate-200 sm:w-1/2" data-testid="reply-to-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email notificaciones internas</Label>
                <Input value={config.notification_email} onChange={e => setConfig(p => ({...p, notification_email: e.target.value}))} placeholder="avisos@ascauto.com" className="rounded-sm h-9 border-slate-200 sm:w-1/2" data-testid="notification-email-input" />
              </div>
            </CardContent>
          </Card>

          {/* Provider */}
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2"><Server className="w-4 h-4 text-red-600" /> Proveedor</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={config.provider} onValueChange={v => setConfig(p => ({...p, provider: v}))}>
                <SelectTrigger className="rounded-sm h-9 w-48 border-slate-200" data-testid="provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="resend">Resend (API)</SelectItem>
                  <SelectItem value="smtp">SMTP directo</SelectItem>
                </SelectContent>
              </Select>
              {config.provider === "resend" && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3">
                  <SensitiveField field="resend_api_key" label="API Key Resend" hint="Clave secreta de Resend. Almacenada cifrada." masked={config.resend_api_key_masked} isSet={config.resend_api_key_set} />
                </div>
              )}
              {config.provider === "smtp" && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Host SMTP</Label>
                      <Input value={config.smtp_host} onChange={e => setConfig(p => ({...p, smtp_host: e.target.value}))} className="rounded-sm h-9 border-slate-200" data-testid="smtp-host-input" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Puerto</Label>
                      <Input type="number" value={config.smtp_port} onChange={e => setConfig(p => ({...p, smtp_port: parseInt(e.target.value) || 587}))} className="rounded-sm h-9 border-slate-200" data-testid="smtp-port-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Usuario SMTP</Label>
                    <Input value={config.smtp_username} onChange={e => setConfig(p => ({...p, smtp_username: e.target.value}))} className="rounded-sm h-9 border-slate-200" data-testid="smtp-user-input" />
                  </div>
                  <SensitiveField field="smtp_password" label="Password SMTP" hint="Almacenada cifrada." masked={config.smtp_password_masked} isSet={config.smtp_password_set} />
                  <div className="flex items-center gap-2">
                    <Switch checked={config.smtp_tls} onCheckedChange={v => setConfig(p => ({...p, smtp_tls: v}))} data-testid="smtp-tls-toggle" />
                    <Label className="text-xs text-slate-600">Usar TLS</Label>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/50 rounded-sm">
            <CardContent className="p-4 flex items-start gap-3">
              <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800 space-y-1">
                <p className="font-bold">Seguridad</p>
                <p>Los campos sensibles se almacenan <strong>cifrados</strong> (AES-128). Solo superadministradores pueden acceder.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ TAB: PLANTILLAS ═══ */}
        <TabsContent value="templates" className="space-y-4 mt-4">
          {tplLoading ? (
            <div className="py-12 text-center text-sm text-slate-400">Cargando plantillas...</div>
          ) : templates.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">No hay plantillas configuradas</div>
          ) : (
            templates.map(tpl => {
              const meta = typeLabels[tpl.type] || { label: tpl.type, desc: "", icon: Mail };
              const Icon = meta.icon;
              return (
                <Card key={tpl.type} className="border-slate-200 rounded-sm" data-testid={`template-${tpl.type}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm uppercase tracking-wider font-semibold flex items-center gap-2">
                      <Icon className="w-4 h-4 text-red-600" /> {meta.label}
                    </CardTitle>
                    <p className="text-xs text-slate-500">{meta.desc}</p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Asunto</Label>
                      <Input value={tpl.subject || ""} onChange={e => updateTpl(tpl.type, "subject", e.target.value)} className="rounded-sm h-9 border-slate-200" data-testid={`tpl-subject-${tpl.type}`} />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto introduccion (HTML)</Label>
                      <Textarea value={tpl.body_intro || ""} onChange={e => updateTpl(tpl.type, "body_intro", e.target.value)} className="rounded-sm border-slate-200 text-sm font-mono" rows={2} data-testid={`tpl-intro-${tpl.type}`} />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Texto pie / nota</Label>
                      <Textarea value={tpl.body_footer || ""} onChange={e => updateTpl(tpl.type, "body_footer", e.target.value)} className="rounded-sm border-slate-200 text-sm font-mono" rows={2} data-testid={`tpl-footer-${tpl.type}`} />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Telefono en email</Label>
                      <Input value={tpl.phone_display || ""} onChange={e => updateTpl(tpl.type, "phone_display", e.target.value)} placeholder="916003800" className="rounded-sm h-9 border-slate-200 w-48" data-testid={`tpl-phone-${tpl.type}`} />
                    </div>
                    <Button onClick={() => handleSaveTpl(tpl)} disabled={savingTpl === tpl.type} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider h-8 px-4" data-testid={`save-tpl-${tpl.type}`}>
                      <Save className="w-3 h-3 mr-1" /> {savingTpl === tpl.type ? "Guardando..." : "Guardar"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* ═══ TAB: INVENTARIO ═══ */}
        <TabsContent value="inventory" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={invSearch} onChange={e => setInvSearch(e.target.value)} placeholder="Buscar email, nombre, funcion..." className="pl-8 rounded-sm h-8 text-xs border-slate-200" data-testid="inv-search" />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[140px] rounded-sm h-8 text-xs border-slate-200"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {[...new Set(invData.emails.map(e => e.category))].sort().map(cat => (
                  <SelectItem key={cat} value={cat}>{CAT_META[cat]?.label || cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={exportCSV} className="rounded-sm text-[10px] uppercase h-8 px-3" data-testid="export-inv-btn">
              <Download className="w-3 h-3 mr-1" /> CSV
            </Button>
            {(invSearch || filterCategory !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => { setInvSearch(""); setFilterCategory("all"); }} className="h-8 px-2 text-xs text-slate-400">
                <X className="w-3 h-3 mr-1" /> Limpiar
              </Button>
            )}
          </div>

          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-2 px-4 pt-3">
              <CardDescription className="text-[10px] uppercase tracking-wider font-semibold">{filteredEmails.length} de {invData.total} emails</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {invLoading ? (
                <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredEmails.map((e, i) => {
                    const catMeta = CAT_META[e.category] || { label: e.category, icon: Mail, color: "bg-slate-50 text-slate-600 border-slate-200" };
                    const CatIcon = catMeta.icon;
                    return (
                      <div key={i} className="p-4 hover:bg-slate-50/50 transition-colors" data-testid={`inv-row-${i}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${catMeta.color.split(" ")[0]}`}>
                            <CatIcon className="w-4 h-4 text-slate-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="text-sm font-bold text-slate-900">{e.name}</span>
                              <span className={`inline-flex items-center text-[9px] uppercase tracking-wider font-semibold border px-1.5 py-0.5 rounded-sm ${catMeta.color}`}>{catMeta.label}</span>
                              {e.active ? (
                                <Badge variant="outline" className="h-4 text-[9px] border-emerald-200 text-emerald-700 bg-emerald-50 rounded-sm"><CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Activo</Badge>
                              ) : (
                                <Badge variant="outline" className="h-4 text-[9px] border-slate-200 text-slate-400 rounded-sm"><XCircle className="w-2.5 h-2.5 mr-0.5" /> Inactivo</Badge>
                              )}
                              {e.editable ? (
                                <Badge variant="outline" className="h-4 text-[9px] border-blue-200 text-blue-600 bg-blue-50 rounded-sm"><Pencil className="w-2.5 h-2.5 mr-0.5" /> Editable</Badge>
                              ) : (
                                <Badge variant="outline" className="h-4 text-[9px] border-slate-200 text-slate-400 rounded-sm"><Lock className="w-2.5 h-2.5 mr-0.5" /> Solo lectura</Badge>
                              )}
                            </div>
                            <code className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded-sm">{e.address}</code>
                            <p className="text-xs text-slate-500 mt-1">{e.purpose}</p>
                            <div className="flex items-center gap-4 mt-1 flex-wrap">
                              <span className="text-[10px] text-slate-400"><strong>Flujo:</strong> {e.flow}</span>
                              <span className="text-[10px] text-slate-400"><strong>Fuente:</strong> {e.source}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ═══ DIALOGS ═══ */}
      <Dialog open={confirmSave} onOpenChange={setConfirmSave}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Confirmar cambios</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Se guardaran los cambios en la configuracion de email.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSave(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="confirm-save-btn">
              {saving ? "Guardando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-blue-500" /> Email de prueba</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Remitente: {config.sender_name} &lt;{config.sender_email}&gt;</Label>
              <p className="text-[10px] text-slate-400 mt-1">
                Proveedor activo: <strong className="text-slate-600">{config.provider === "smtp" ? "SMTP directo" : "Resend (API)"}</strong>
              </p>
            </div>
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="tu@email.com" className="rounded-sm h-9 border-slate-200" data-testid="test-email-input" />
            {testResult && (
              <div className={`p-3 rounded-sm text-xs flex items-start gap-2 ${testResult.status === "enviado" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-red-50 border border-red-200 text-red-800"}`} data-testid="test-result">
                {testResult.status === "enviado" ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <XCircle className="w-4 h-4 flex-shrink-0" />}
                <span>{testResult.message}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTestDialog(false); setTestResult(null); }} className="rounded-sm">Cerrar</Button>
            <Button onClick={handleTest} disabled={testing} className="bg-blue-600 text-white hover:bg-blue-700 rounded-sm" data-testid="send-test-btn">
              {testing ? "Enviando..." : "Enviar prueba"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAudit} onOpenChange={setShowAudit}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-slate-500" /> Historial de cambios</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {auditLogs.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No hay registros</p>
            ) : auditLogs.map((log, i) => (
              <div key={log.id || i} className="border border-slate-200 rounded-sm p-3 text-xs" data-testid={`audit-entry-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`h-4 text-[8px] rounded-sm ${
                      log.action === "update" ? "border-blue-200 text-blue-600 bg-blue-50" :
                      log.action === "reveal" ? "border-amber-200 text-amber-600 bg-amber-50" :
                      log.action === "test_email" ? "border-emerald-200 text-emerald-600 bg-emerald-50" :
                      "border-slate-200 text-slate-600"
                    }`}>{log.action}</Badge>
                    <span className="text-slate-500"><User className="w-2.5 h-2.5 inline mr-0.5" />{log.user}</span>
                  </div>
                  <span className="text-slate-400">{log.timestamp ? new Date(log.timestamp).toLocaleString("es-ES") : ""}</span>
                </div>
                {log.changes?.map((ch, j) => (
                  <div key={j} className="ml-4 text-[10px] text-slate-600">
                    <span className="font-mono font-semibold">{ch.field}</span>: {ch.old || "(vacio)"} &rarr; {ch.new}
                  </div>
                ))}
                {log.action === "reveal" && <p className="ml-4 text-[10px] text-amber-600">Revelo: {log.field}</p>}
                {log.action === "test_email" && <p className="ml-4 text-[10px] text-emerald-600">Test a: {log.to} ({log.result})</p>}
                {log.action === "update" && (
                  <button onClick={() => handleRestore(log.id)} className="ml-4 mt-1 text-[10px] text-blue-500 hover:text-blue-700 flex items-center gap-1">
                    <RotateCcw className="w-2.5 h-2.5" /> Restaurar
                  </button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
